'use client';

import clsx from 'clsx';

import type { MouseEventHandler, ReactElement, ReactNode } from 'react';
import { cloneElement } from 'react';

import Link from 'next/link';

import type { CarbonIconProps } from '@carbon/icons-react';
import { ArrowRight, ArrowUpRight } from '@carbon/icons-react';
import type { LinkProps } from '@carbon/react';
import { Link as CarbonLink } from '@carbon/react';

import { getLinkProps, getUrlType } from '@/utils/links';

import styles from './interactive-link.module.scss';

export type Variant = 'primary' | 'secondary' | 'caption';

export interface Props {
  size?: LinkProps<'a'>['size'];
  variant?: Variant;
  url?: string;
  label: ReactNode;
  hasIcon?: boolean;
  customIcon?: ReactElement<CarbonIconProps>;
  iconPosition?: 'start' | 'end';
  onClick?: MouseEventHandler<HTMLAnchorElement>;
  className?: string;
  target?: string;
  'aria-label'?: string;
  rel?: string;
  narrow?: boolean;
  theme?: 'light' | 'dark';
  'data-autotrack-cta'?: string;
  'data-modal-primary-focus'?: boolean;
}

export function InteractiveLink(props: Props) {
  const {
    variant = 'primary',
    size,
    url,
    label,
    hasIcon = false,
    customIcon,
    iconPosition = 'end',
    onClick,
    className,
    target: customTarget,
    rel: customRel,
    narrow = false,
    theme = 'light',
    'data-autotrack-cta': autotrackCta,
    'data-modal-primary-focus': dataModalPrimaryFocus,
  } = props;
  const urlType = getUrlType(url);
  const linkProps = getLinkProps(url);
  const target = customTarget ?? linkProps.target;
  const rel = customRel ?? linkProps.rel;
  const isExternalUrl = urlType === 'corporate-external' || urlType === 'external';
  const isInternalUrl = urlType === 'internal';

  const interactiveLinkClasses = clsx(
    styles['interactive-link'],
    styles[`interactive-link--${theme}`],
    styles[`interactive-link--${variant}`],
    styles[`interactive-link--${size}`],
    {
      [styles[`interactive-link--narrow`]]: narrow,
    },
    className
  );
  const interactiveLinkSize = size || variant === 'caption' ? 'md' : 'lg';
  const iconSize = interactiveLinkSize === 'lg' ? 20 : 16;

  if ((isInternalUrl || iconPosition === 'start') && url) {
    return (
      <Link
        {...linkProps}
        aria-label={props['aria-label']}
        className={interactiveLinkClasses}
        href={url}
        onClick={onClick}
        target={target}
        rel={rel}
        data-autotrack-cta={autotrackCta}
        data-modal-primary-focus={dataModalPrimaryFocus}
      >
        {hasIcon && iconPosition === 'start' && (
          <InteractiveLinkIcon
            customIcon={customIcon}
            isExternalUrl={isExternalUrl}
            iconPosition={iconPosition}
            size={iconSize}
          />
        )}
        {label}
        {hasIcon && iconPosition === 'end' && (
          <InteractiveLinkIcon
            customIcon={customIcon}
            isExternalUrl={isExternalUrl}
            iconPosition={iconPosition}
            size={iconSize}
          />
        )}
        {target === '_blank' && <span className="sr-only">(opens in a new tab)</span>}
      </Link>
    );
  }

  return (
    <CarbonLink
      {...linkProps}
      aria-label={props['aria-label']}
      rel={rel}
      target={target}
      href={url}
      size={size ?? interactiveLinkSize}
      className={interactiveLinkClasses}
      onClick={onClick}
      data-autotrack-cta={autotrackCta}
      data-modal-primary-focus={dataModalPrimaryFocus}
    >
      {label}
      {hasIcon && (
        <InteractiveLinkIcon
          customIcon={customIcon}
          isExternalUrl={isExternalUrl}
          iconPosition={iconPosition}
          size={iconSize}
        />
      )}
      {target === '_blank' && <span className="sr-only">(opens in a new tab)</span>}
    </CarbonLink>
  );
}

function InteractiveLinkIcon(props: {
  customIcon?: ReactElement<CarbonIconProps>;
  isExternalUrl: boolean;
  iconPosition: 'start' | 'end';
  size: number;
}) {
  const { customIcon, isExternalUrl, iconPosition, size } = props;
  const iconClassName = clsx(
    styles['interactive-link__icon'],
    styles[`interactive-link__icon--${iconPosition}`]
  );

  if (customIcon) {
    return cloneElement(customIcon, {
      ...customIcon.props,
      className: iconClassName,
    });
  }

  if (isExternalUrl) {
    return <ArrowUpRight className={iconClassName} size={size} />;
  }

  return <ArrowRight className={iconClassName} size={size} />;
}
